import { useState } from "react";
import { useIntl } from "react-intl";
import { Badge } from "~/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { ReportStatusIcon } from "./ReportStatusIcon";

export type TemplateReportStatus =
  | "notStarted"
  | "pending"
  | "inProgress"
  | "completed"
  | "failed";

export type OutdatedReason = "outdatedByDoclist" | "outdatedByTemplate";

export type TemplateReportItem = {
  id: string;
  name: string;
  status: TemplateReportStatus;
  isOutdated: boolean;
  outdatedReason?: OutdatedReason;
};

export const TemplateReportSelector = ({
  templateReportItems,
  selectedTemplateReportIndex,
  onValueChange,
  onRegenerateClick,
}: {
  templateReportItems: TemplateReportItem[];
  selectedTemplateReportIndex: number;
  onValueChange: (index: number) => void;
  onRegenerateClick: (report: TemplateReportItem) => void;
}) => {
  const { formatMessage } = useIntl();
  const [selectOpen, setSelectOpen] = useState(false);

  const templateReport =
    templateReportItems.length > selectedTemplateReportIndex
      ? templateReportItems[selectedTemplateReportIndex]
      : null;

  const handleValueChange = (value: string) => {
    const index = templateReportItems.findIndex(
      (report) => report.id === value
    );
    if (index !== -1) {
      trackEvent({
        eventName: "app/report/template_selector.select/change",
        properties: {
          templateId: value,
          templateName: templateReportItems[index].name,
        },
      });
      onValueChange(index);
    }
  };

  if (!templateReport) {
    return (
      <div className="mb-3 w-full text-center text-muted-foreground p-4">
        {formatMessage({
          id: "chat.report.noTemplates",
          defaultMessage:
            "No report templates available. Create one in Settings.",
        })}
      </div>
    );
  }

  return (
    <div className="mb-3 w-full">
      <Select
        value={templateReport.id}
        onValueChange={handleValueChange}
        open={selectOpen}
        onOpenChange={setSelectOpen}
      >
        <SelectTrigger className="w-full">
          <SelectValue
            placeholder={formatMessage({
              id: "chat.report.selectorPlaceholder",
              defaultMessage: "Report",
            })}
          >
            {templateReport.name}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
          {templateReportItems.map((report, index) => {
            const isSelected = selectedTemplateReportIndex === index;

            return (
              <SelectItem
                key={report.id}
                value={report.id}
                className="[&>span:first-child]:hidden"
              >
                <div className="w-full flex justify-between items-center gap-2">
                  <span
                    className={cn(
                      "flex-1 min-w-0 break-words",
                      isSelected && "font-semibold text-muted-foreground"
                    )}
                  >
                    {report.name}
                  </span>
                  <div className="flex items-center gap-2">
                    {report.isOutdated && (
                      <Badge
                        variant="secondary"
                        className="text-xs px-2 py-0 h-5 border-amber-200 bg-amber-50 text-amber-800 font-normal"
                      >
                        {formatMessage({ id: "chat.report.outdated" })}
                      </Badge>
                    )}
                    <ReportStatusIcon
                      status={report.status}
                      onRegenerate={() => {
                        setSelectOpen(false);
                        onRegenerateClick(report);
                      }}
                    />
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
